import { SmsMessageType } from '@app/v2/shared/enums/sms-message.enum'
import { SmsInsertChip, SmsPlaceholder, SmsPreviewEvent, SmsSegmentInfo } from './sms-editor.type'

export class SmsMessageModel {
  /** Characters in a single GSM (plain text) SMS. */
  static readonly GSM_SINGLE_LIMIT = 160

  /** Characters per part once a GSM message is split; each part keeps room for the header that joins the parts. */
  static readonly GSM_PART_LIMIT = 153

  /** Characters in a single Unicode SMS (Arabic, emoji, or any non-GSM character). */
  static readonly UNICODE_SINGLE_LIMIT = 70

  /** Characters per part once a Unicode message is split. */
  static readonly UNICODE_PART_LIMIT = 67

  static readonly SAMPLE_GUEST_FULL_NAME = 'John Doe'
  static readonly SAMPLE_GUEST_FIRST_NAME = 'John'

  /** Real confirmation and invitation links are mt.events plus a random 8-character code, so the sample has the real length. */
  static readonly SAMPLE_SHORT_LINK = 'https://mt.events/k7Qm2nXp'
  static readonly SAMPLE_BARCODE = '19PACZAL'

  static readonly DEFAULT_EVENT_NAME = 'Event'
  static readonly DEFAULT_EVENT_SLUG = 'event'

  /** Matches any `{{placeholder}}`, known or not. */
  static readonly PLACEHOLDER_PATTERN = /\{\{([^{}]+)\}\}/g

  private static readonly _GUEST_PLACEHOLDERS: SmsPlaceholder[] = [
    { key: 'firstname', label: 'First name', sample: SmsMessageModel.SAMPLE_GUEST_FIRST_NAME, isLink: false },
    { key: 'fullname', label: 'Full name', sample: SmsMessageModel.SAMPLE_GUEST_FULL_NAME, isLink: false }
  ]

  private static readonly _TYPE_PLACEHOLDERS: Record<SmsMessageType, SmsPlaceholder[]> = {
    [SmsMessageType.CONFIRMATION]: [
      { key: 'confirmationUrl', label: 'Confirmation link', sample: SmsMessageModel.SAMPLE_SHORT_LINK, isLink: true },
      { key: 'barcode', label: 'QR code', sample: SmsMessageModel.SAMPLE_BARCODE, isLink: false }
    ],
    [SmsMessageType.INVITATION]: [{ key: 'invitationUrl', label: 'Invitation link', sample: SmsMessageModel.SAMPLE_SHORT_LINK, isLink: true }]
  }

  /** Old placeholder names and the current placeholder each one maps to. */
  private static readonly _LEGACY_PLACEHOLDERS: Record<string, string> = {
    first_name: '{{firstname}}',
    confirmation_link: '{{confirmationUrl}}',
    qr_link: '{{barcode}}',
    invitation_link: '{{invitationUrl}}'
  }

  /** Old event placeholders, which are now written as the event's real name or slug. */
  private static readonly _LEGACY_EVENT_FIELDS: Record<string, keyof SmsPreviewEvent> = {
    event_name: 'name',
    event_slug: 'slug',
    event_shortname: 'slug'
  }

  private static readonly _LEGACY_PATTERN = SmsMessageModel._legacyPattern()

  private static readonly _GSM_BASIC = new Set('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà')

  /** GSM characters that take two characters of space each. */
  private static readonly _GSM_EXTENDED = new Set('\f^{}\\[~]|€')

  private static readonly _ARABIC_LETTER = /[\u0600-\u06FF]/
  private static readonly _LATIN_LETTER = /[A-Za-z]/

  /**
   * Placeholders the backend fills in for this message type.
   * @param type Confirmation or invitation
   */
  static placeholders(type: SmsMessageType): SmsPlaceholder[] {
    return [...SmsMessageModel._GUEST_PLACEHOLDERS, ...SmsMessageModel._TYPE_PLACEHOLDERS[type]]
  }

  /**
   * Placeholder names the backend fills in for this message type.
   * @param type Confirmation or invitation
   */
  static knownKeys(type: SmsMessageType): Set<string> {
    return new Set(SmsMessageModel.placeholders(type).map((placeholder) => placeholder.key))
  }

  /**
   * Chips shown under the editor. Event name and short name insert the event's real values, not placeholders.
   * @param type Confirmation or invitation
   * @param event Event that owns the message
   */
  static insertChips(type: SmsMessageType, event: SmsPreviewEvent | null): SmsInsertChip[] {
    const toChip = (placeholder: SmsPlaceholder): SmsInsertChip => ({ label: placeholder.label, value: SmsMessageModel.token(placeholder.key), isLink: placeholder.isLink })
    return [
      ...SmsMessageModel._GUEST_PLACEHOLDERS.map(toChip),
      { label: 'Event name', value: SmsMessageModel.eventName(event), isLink: false },
      { label: 'Event short name', value: SmsMessageModel.eventSlug(event), isLink: false },
      ...SmsMessageModel._TYPE_PLACEHOLDERS[type].map(toChip)
    ]
  }

  /**
   * Wraps a placeholder name in double braces.
   * @param key Placeholder name such as `firstname`
   */
  static token(key: string): string {
    return `{{${key}}}`
  }

  /**
   * Event name written into the message, with a fallback while the event is loading.
   * @param event Event that owns the message
   */
  static eventName(event: SmsPreviewEvent | null): string {
    return event?.name || SmsMessageModel.DEFAULT_EVENT_NAME
  }

  /**
   * Event slug written into the message, with a fallback while the event is loading.
   * @param event Event that owns the message
   */
  static eventSlug(event: SmsPreviewEvent | null): string {
    return event?.slug || SmsMessageModel.DEFAULT_EVENT_SLUG
  }

  /**
   * Default body with the event name written as plain text.
   * @param type Confirmation or invitation
   * @param event Event that owns the message
   */
  static defaultBody(type: SmsMessageType, event: SmsPreviewEvent | null): string {
    const eventName = SmsMessageModel.eventName(event)
    if (type === SmsMessageType.INVITATION) return `Hi {{firstname}}, This is your invitation to ${eventName}. Invitation link: {{invitationUrl}}`
    return `Hi {{firstname}}, This is your confirmation to ${eventName}. Code is {{barcode}}\nConfirmation link: {{confirmationUrl}}`
  }

  /**
   * Replaces old `{first_name}` / `{{first_name}}`-style placeholders with current ones, and event placeholders with the event's real text.
   * @param template Saved message
   * @param event Event that owns the message
   */
  static rewriteLegacyPlaceholders(template: string, event: SmsPreviewEvent | null): string {
    return template.replace(SmsMessageModel._LEGACY_PATTERN, (match: string, doubleBraceKey: string | undefined, singleBraceKey: string | undefined) => {
      const key = doubleBraceKey ?? singleBraceKey
      if (!key) return match
      const eventField = SmsMessageModel._LEGACY_EVENT_FIELDS[key]
      if (!eventField) return SmsMessageModel._LEGACY_PLACEHOLDERS[key]
      return eventField === 'name' ? SmsMessageModel.eventName(event) : SmsMessageModel.eventSlug(event)
    })
  }

  /**
   * Fills each known placeholder with its sample value. Unknown placeholders stay as typed, because the backend sends them as typed.
   * @param template Saved message
   * @param type Confirmation or invitation
   */
  static resolveSamples(template: string, type: SmsMessageType): string {
    const samples = new Map(SmsMessageModel.placeholders(type).map((placeholder) => [placeholder.key, placeholder.sample]))
    return template.replace(SmsMessageModel.PLACEHOLDER_PATTERN, (match: string, key: string) => samples.get(key) ?? match)
  }

  /**
   * Billed length and SMS count. Any non-GSM character switches the whole message to Unicode (70 per SMS);
   * GSM extended characters such as `{` or `€` take two characters each.
   * @param text Message as the guest receives it
   */
  static segmentInfo(text: string): SmsSegmentInfo {
    let gsmLength = 0
    for (const char of text) {
      if (SmsMessageModel._GSM_BASIC.has(char)) gsmLength += 1
      else if (SmsMessageModel._GSM_EXTENDED.has(char)) gsmLength += 2
      else return SmsMessageModel._segmentInfo(text.length, true)
    }
    return SmsMessageModel._segmentInfo(gsmLength, false)
  }

  /**
   * True when the first letter is Arabic, so an Arabic-leading message reads right to left.
   * @param text Message to check
   */
  static startsWithArabic(text: string): boolean {
    for (const char of text) {
      if (SmsMessageModel._ARABIC_LETTER.test(char)) return true
      if (SmsMessageModel._LATIN_LETTER.test(char)) return false
    }
    return false
  }

  /**
   * Editor direction: placeholders and the event name/slug are ignored, so they do not count as English.
   * @param template Message in the editor
   * @param event Event that owns the message
   */
  static isEditorRtl(template: string, event: SmsPreviewEvent | null): boolean {
    let text = template.replace(SmsMessageModel.PLACEHOLDER_PATTERN, '')
    const eventTexts = [SmsMessageModel.eventName(event), SmsMessageModel.eventSlug(event)].filter((value) => value.length > 1).sort((a, b) => b.length - a.length)
    for (const value of eventTexts) text = text.split(value).join('')
    return SmsMessageModel.startsWithArabic(text)
  }

  /**
   * Counts SMS parts: one when the message fits a single SMS, otherwise split by the smaller per-part limit.
   * @param length Billed length
   * @param isUnicode True when the message is sent as Unicode
   */
  private static _segmentInfo(length: number, isUnicode: boolean): SmsSegmentInfo {
    const singleLimit = isUnicode ? SmsMessageModel.UNICODE_SINGLE_LIMIT : SmsMessageModel.GSM_SINGLE_LIMIT
    const partLimit = isUnicode ? SmsMessageModel.UNICODE_PART_LIMIT : SmsMessageModel.GSM_PART_LIMIT
    const segments = !length ? 0 : length <= singleLimit ? 1 : Math.ceil(length / partLimit)
    return { length, isUnicode, segments }
  }

  /**
   * Matches `{{key}}` or `{key}` for every old placeholder name.
   */
  private static _legacyPattern(): RegExp {
    const keys = [...Object.keys(SmsMessageModel._LEGACY_PLACEHOLDERS), ...Object.keys(SmsMessageModel._LEGACY_EVENT_FIELDS)].join('|')
    return new RegExp(`\\{\\{(${keys})\\}\\}|\\{(${keys})\\}`, 'g')
  }
}
